/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/test-ids';
import classNames from 'classnames';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import { memberId } from '@trello/session-cookie';
import { idToDate } from '@trello/dates';
import { getMilliseconds } from '@trello/time';
import { HeaderButton } from 'app/src/components/Header/HeaderButton';
// eslint-disable-next-line no-restricted-imports
import { State } from 'app/gamma/src/modules/types';
// eslint-disable-next-line no-restricted-imports
import {
  loadNotificationGroups,
  loadUnreadNotificationsCount,
} from 'app/gamma/src/modules/loaders/load-notifications';
import React, {
  createContext,
  Suspense,
  FunctionComponent,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FavIcon } from 'app/scripts/lib/favicon';
// eslint-disable-next-line no-restricted-imports
import { getUnseenNotificationsCount } from 'app/gamma/src/selectors/notifications';
import styles from './NotificationsMenuButton.less';
import { usePopover, Popover } from '@trello/nachos/popover';
import { forTemplate } from '@trello/i18n';
import { useLazyComponent } from '@trello/use-lazy-component';
import { NotificationIcon } from '@trello/nachos/icons/notification';
import { SmartLinkAnalyticsContextProvider } from 'app/src/components/SmartMedia';
import {
  useNotificationsMenuButtonQuery,
  NotificationsMenuButtonQuery,
} from './NotificationsMenuButtonQuery.generated';
const formatHeaderUser = forTemplate('header_user');

const NOTIFICATIONS_POPOVER_WIDTH = 432;

export interface NotificationsPopoverContextProps {
  hide: () => void;
  showPlanSelection: (idOrganization?: string) => void;
}

export const NotificationsPopoverContext = createContext<NotificationsPopoverContextProps>(
  {
    hide: () => {},
    showPlanSelection: () => {},
  },
);

const memberHasOtherNotifications = (
  member?: NotificationsMenuButtonQuery['member'],
) => {
  if (!member) {
    return false;
  }

  const accountCreationDate = idToDate(member.id);
  const isMoreThanThreeDaysOld =
    new Date().getTime() - accountCreationDate.getTime() >
    getMilliseconds({ days: 3 });

  // Should show marketing opt-in, as the user has not set their marketing email preference.
  if (
    !!member.confirmed &&
    isMoreThanThreeDaysOld &&
    !member.marketingOptIn?.date
  ) {
    return true;
  }

  const hasDismissedMessage = !!(member.messagesDismissed || []).find(
    (message) => message.name === '1-nov-2018-tos-change-accepted',
  );

  //  Show notice of ToS change if the user's account was created before
  //  November 1, 2018 and the user has not already dismissed the message by
  //  clicking "I Agree"
  return !hasDismissedMessage && new Date(2018, 10, 1) > accountCreationDate;
};

export const NotificationsMenuButton: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { unreadNotificationsCount } = useSelector((state: State) => {
    return {
      unreadNotificationsCount: getUnseenNotificationsCount(state),
    };
  });
  const { data } = useNotificationsMenuButtonQuery({
    variables: {
      memberId: memberId || 'me',
    },
  });
  const hasOtherNotifications = memberHasOtherNotifications(data?.member);

  const onLoadNotificationGroups = useCallback(() => {
    dispatch(loadNotificationGroups());
  }, [dispatch]);
  const onLoadUnreadNotificationsCount = useCallback(() => {
    dispatch(loadUnreadNotificationsCount());
  }, [dispatch]);

  const NotificationsMenu = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "notifications-menu-popover" */ 'app/src/components/NotificationsMenu'
      ),
    { namedImport: 'NotificationsMenu' },
  );
  const PlanSelectionOverlay = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "notifications-plan-selection-overlay" */ 'app/src/components/FreeTrial'
      ),
    {
      namedImport: 'PlanSelectionOverlay',
    },
  );

  useEffect(() => {
    onLoadUnreadNotificationsCount();
    onLoadNotificationGroups();
  }, [onLoadNotificationGroups, onLoadUnreadNotificationsCount]);

  useEffect(() => {
    if (!hasOtherNotifications && unreadNotificationsCount > 0) {
      FavIcon.setNotifications(true);
    }

    if (!hasOtherNotifications && unreadNotificationsCount === 0) {
      FavIcon.setNotifications(false);
    }
  }, [hasOtherNotifications, unreadNotificationsCount]);

  const onShow = useCallback(() => {
    Analytics.sendScreenEvent({
      name: 'notificationsInlineDialog',
    });
  }, []);

  const onHide = useCallback(() => {
    Analytics.sendClosedComponentEvent({
      componentType: 'inlineDialog',
      componentName: 'notificationsInlineDialog',
      source: getScreenFromUrl(),
    });
  }, []);

  const {
    triggerRef,
    toggle,
    hide,
    popoverProps,
  } = usePopover<HTMLButtonElement>({
    onShow,
    onHide,
  });

  const [isPlanSelectionOverlayOpen, togglePlanSelectionOverlay] = useState(
    false,
  );

  // string planSelectionOrgId is set by the individual child notification component
  const [planSelectionOrgId, setPlanSelectionOrgId] = useState('');

  const showPlanSelection = (orgId?: string) => {
    setPlanSelectionOrgId(orgId ?? '');
    togglePlanSelectionOverlay(true);
  };

  return (
    <>
      <HeaderButton
        icon={<NotificationIcon color="light" />}
        onClick={toggle}
        ref={triggerRef}
        testId={HeaderTestIds.NotificationsButton}
        className={classNames(styles.headerButton, {
          [styles.newNotifications]:
            !hasOtherNotifications && unreadNotificationsCount > 0,
          [styles.hasOtherNotifications]: hasOtherNotifications,
        })}
        ariaLabel={formatHeaderUser('notifications-menu')}
      />
      <SmartLinkAnalyticsContextProvider
        value={{ source: 'notificationsInlineDialog' }}
      >
        <NotificationsPopoverContext.Provider
          value={{ hide, showPlanSelection }}
        >
          <Popover
            {...popoverProps}
            title={formatHeaderUser('notifications-menu')}
            noHorizontalPadding
            testId={HeaderTestIds.NotificationsPopover}
            dangerous_width={NOTIFICATIONS_POPOVER_WIDTH}
          >
            <Suspense fallback={null}>
              <NotificationsMenu />
            </Suspense>
          </Popover>
          {isPlanSelectionOverlayOpen && (
            <Suspense fallback={null}>
              <PlanSelectionOverlay
                // eslint-disable-next-line react/jsx-no-bind
                onClose={() => {
                  togglePlanSelectionOverlay(false);
                  setPlanSelectionOrgId('');
                }}
                orgId={planSelectionOrgId}
                showDefault={true}
              />
            </Suspense>
          )}
        </NotificationsPopoverContext.Provider>
      </SmartLinkAnalyticsContextProvider>
    </>
  );
};
